import React from 'react';
import SEO from '../components/SEO';
import './Privacy.css';

/**
 * Privacy Policy Page Component
 * Details lockitup's privacy practices and data policies
 * Explains what data is NOT collected and website cookie usage
 * Includes SEO meta tags for optimal search engine visibility
 */
function Privacy() {
  return (
    <div className="page-container privacy-page">
      {/* SEO Meta Tags */}
      <SEO 
        title="Privacy Policy – lockitup"
        description="Read lockitup's privacy policy. We do not collect or share personal data. Your privacy is fully protected."
        keywords="lockitup privacy policy, data privacy, no data collection, secure software"
        ogTitle="Privacy Policy – lockitup"
        ogDescription="Read lockitup's privacy policy. We do not collect or share personal data. Your privacy is fully protected."
        ogUrl="https://www.lockitup.tech/privacy"
      />
      {/* Page Header */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            {/* Shield Icon */}
            <div className="page-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M40 10L15 20V35C15 50 25 62.5 40 70C55 62.5 65 50 65 35V20L40 10Z" stroke="#27AE60" strokeWidth="3" fill="rgba(39, 174, 96, 0.1)" strokeLinejoin="round" />
                <path d="M30 40L37 47L52 32" stroke="#F1C40F" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            
            {/* Page Title */}
            <h1 className="page-title">Privacy Policy</h1>
            <p className="page-subtitle">Your privacy is our top priority</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="page-content">
        <div className="container">
          <div className="content-wrapper">
            
            {/* Main Statement */}
            <div className="highlight-box">
              <h2 className="highlight-title">We do not collect, store, or share any personal data.</h2>
              <p className="highlight-text">
                The lockitup software works completely offline on your device.
              </p>
            </div>

            {/* Information NOT Collected */}
            <div className="content-section">
              <h2 className="section-heading">Information We Do NOT Collect:</h2>
              
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-icon no-collect">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="15" r="13" stroke="#27AE60" strokeWidth="2" />
                      <path d="M8 8L22 22M22 8L8 22" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Personal files or documents</h3>
                </div>

                <div className="info-item">
                  <div className="info-icon no-collect">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="15" r="13" stroke="#27AE60" strokeWidth="2" />
                      <path d="M8 8L22 22M22 8L8 22" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Passwords</h3>
                </div>

                <div className="info-item">
                  <div className="info-icon no-collect">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="15" r="13" stroke="#27AE60" strokeWidth="2" />
                      <path d="M8 8L22 22M22 8L8 22" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Browsing history</h3>
                </div>

                <div className="info-item">
                  <div className="info-icon no-collect">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="15" r="13" stroke="#27AE60" strokeWidth="2" />
                      <path d="M8 8L22 22M22 8L8 22" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Device data</h3>
                </div>

                <div className="info-item">
                  <div className="info-icon no-collect">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="15" r="13" stroke="#27AE60" strokeWidth="2" />
                      <path d="M8 8L22 22M22 8L8 22" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Any user-identifiable information</h3>
                </div>
              </div>
            </div>

            {/* Website Data */}
            <div className="content-section">
              <h2 className="section-heading">Website Data:</h2>
              <div className="text-card">
                <p className="content-text">
                  Our website may use basic analytics and advertising services (such as Google AdSense) to understand traffic and support free hosting. These services may use cookies according to their own privacy policies.
                </p>
              </div>
            </div>

            {/* Third-Party Services */}
            <div className="content-section">
              <h2 className="section-heading">Third-Party Services:</h2>
              <div className="text-card">
                <p className="content-text">
                  If ads are displayed, third-party providers like Google may use cookies to show relevant advertisements.
                </p>
              </div>
            </div>

            {/* Your Control */}
            <div className="content-section">
              <h2 className="section-heading">Your Control:</h2>
              <div className="text-card highlight">
                <p className="content-text">
                  You can use lockitup without creating an account and without sharing any personal information.
                </p>
              </div>
            </div>

            {/* Agreement */}
            <div className="agreement-box">
              <p className="agreement-text">
                By using our website or software, you agree to this policy.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default Privacy;
